<template>
  <div
    v-if="panelData"
    class="standard-elevation-1 animate-none overflow-visible rounded-[8px] bg-white"
    :class="{ 'expanded-sidebar': drawer }"
    :style="panelPositions['infoPanel']"
  >
    <div v-show="show">
      <div
        class="hide-scrollbar relative flex flex-col items-start overflow-y-auto rounded-[8px]"
        style="width: 366px"
        :style="'max-height:' + containerHeight + 'px'"
      >
        <InfoPanelHeader
          :panel-name="panelData.name"
          :updated-info="panelData.updatedInfo"
          @close-click="show = false"
        />
        <div
          v-if="feature.source === 'heatProjectCentroid' && heatProject"
          class="flex w-full flex-col gap-3 p-2.5"
        >
          <HeatProjectMetaInfo
            :show-params-modal="false"
            :heat-project
            :scenario="panelData.scenarioName"
          >
            <template #options>
              <MoreOptions>
                <div
                  class="standard-elevation-1 absolute right-0 top-0 z-10 flex flex-col overflow-hidden whitespace-nowrap rounded-[4px] border-[1px] border-blue-grey-900 bg-white hover:bg-infra-highlight-25"
                >
                  <div
                    class="body-3 flex cursor-pointer flex-col items-start justify-between"
                  >
                    <div
                      v-if="heatProject.isEditable"
                      class="info-panel-more-options"
                      @click="edit()"
                    >
                      <span>Projektgebiet bearbeiten</span>
                    </div>
                    <div class="info-panel-more-options">
                      <span @click="downloadProjectData(heatProject.id)"
                        >Daten herunterladen</span
                      >
                    </div>
                    <div
                      v-if="heatProject.isDeletable"
                      class="info-panel-more-options"
                      @click="isDialogOpen = true"
                    >
                      <span>löschen</span>
                    </div>
                  </div>
                </div>
              </MoreOptions>
            </template>
          </HeatProjectMetaInfo>
          <div class="standard-elevation-0-dark flex flex-col gap-2 p-2.5">
            <div class="flex justify-between">
              <h6 class="text-infra-highlight-800">Gebäudeselektion</h6>
            </div>
            <div class="relative grid grid-cols-3 gap-1 rounded-[2px]">
              <ProjectPlanerTabContainer
                v-for="(data, index) in tabContainerData"
                :key="index"
                :data="data"
              />
            </div>
          </div>
          <div class="standard-elevation-0-dark rounded-[6px] p-2.5">
            <SliderEl
              :id="heatProject.id"
              v-model.number="heatProject.connectionRate"
              :enabled-max="heatProject.connectionRate"
              :enable-positive-negative-break="breakEven?.connectionRate || 1.1"
              @update:model-value="applyToLayer($event)"
            >
              <template #top>
                <div class="flex justify-between gap-2">
                  <div class="body-3">Zielnetz-Anschlussgrad</div>
                  <div style="color: #0692bc" class="subtitle-2">
                    {{ ProjectConnectionRate * 100 + ' %' }}
                  </div>
                </div>
              </template>
            </SliderEl>
          </div>
        </div>
        <InfoPanelSubtitles
          v-else
          :subtitles="panelData.subtitles"
          :feature="feature"
          @open-dialog="isDialogOpen = true"
        />
        <InfoPanelContent
          :feature="feature"
          :panel-data="panelData"
          @close-info-panel="show = false"
        />
      </div>
    </div>

    <!--delete projektgebiet-->
    <VuetifyDialog
      v-model="isDialogOpen"
      :activator-full-width="true"
      :title="dialogItemType + ' löschen'"
    >
      <template #content>
        <p>{{ heatProject.customDeleteWarning }}</p>
        <p>Dieser Vorgang kann nicht rückgängig gemacht werden.</p>
      </template>
      <template #actions>
        <ButtonEl
          variant="secondary"
          text="Abbrechen"
          @click="isDialogOpen = false"
        />
        <ButtonEl
          text="Löschen"
          color="error"
          @click="
            remove();
            isDialogOpen = false;
          "
        />
      </template>
    </VuetifyDialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import InfoPanelContent from '@/features/map/info-panel/InfoPanelContent.vue';
import InfoPanelHeader from '@/features/map/info-panel/InfoPanelHeader.vue';
import InfoPanelSubtitles from '@/features/map/info-panel/InfoPanelSubtitles.vue';
import { BuildingPanel } from '@/features/map/info-panel/panel-data/building-panel';
import { HeatProjectCentroidPanel } from '@/features/map/info-panel/panel-data/heat-project-centroid-panel';
import { SimplePanel } from '@/features/map/info-panel/panel-data/simple-panel';
import { AggregationPanel } from '@/features/map/info-panel/panel-data/aggregation-panel';
import { PotentialNetworkClusterPanel } from '@/features/map/info-panel/panel-data/potential-network-cluster-panel';
import { ScenarioNetworkClusterPanel } from '@/features/map/info-panel/panel-data/scenario-network-cluster-panel';
import { DistributionNetworkSegmentPanel } from '@/features/map/info-panel/panel-data/distibution-network-segment-panel';
import { BaseDataPanel } from '@/features/map/info-panel/panel-data/base-data';
import { getMap, setFilter } from '@/mapbox/main';
import {
  LAYER_KEY__HEAT_PROJECT_CENTROID,
  LAYER_KEY__HEAT_PROJECT_ESTATE,
  LAYER_KEY__HEAT_PROJECT_NETWORK,
} from '@/configs/layers/heatProject';
import { LAYER_KEY__BUILDINGS } from '@/configs/layers/buildings';
import { highlightBuildings } from '@/features/heat-project/highlight-buildings';
import HeatProjectMetaInfo from '@/features/heat-project/components/HeatProjectMetaInfo.vue';
import MoreOptions from '@/components/storybook/src/stories/moreOptions/MoreOptions.vue';
import ProjectPlanerTabContainer from '@/features/heat-project/components/ProjectPlanerTabContainer.vue';
import SliderEl from '@/components/storybook/src/stories/SliderEl/SliderEl.vue';
import VuetifyDialog from '@/components/storybook/src/stories/vuetifyDialog/VuetifyDialog.vue';
import ButtonEl from '@/components/storybook/src/stories/button/ButtonEl.vue';
import { AutoGeneratedPanel } from '@/features/map/info-panel/panel-data/auto-generated-panel';
import { usePanel } from '@/composables/usePanel';

const props = defineProps({ feature: Object, featureId: [Number, String] });

const show = ref(false);

const { panelPositions, togglePanel } = usePanel('infoPanel', true);

watch(show, () => {
  togglePanel();
});

const featureData = ref(null);
const scenarioYear = computed(() => {
  return store.state.map.scenarioYear;
});
const featureRefresh = computed(() => {
  return store.state.map.refreshFeature;
});
const layerConfig = computed(() => {
  return store.state.map.layerConfigs;
});

const scenarios = computed(() => store.state.scenario.scenarios);

const drawer = computed(() => store.state.layout.drawer);
const containerHeight = computed(() => store.state.map.containerHeight);

const store = useStore();
const panelData = ref(null);
const panel = ref(null);

const panelObjects = [
  new BuildingPanel(),
  new AggregationPanel(),
  new SimplePanel(),
  new HeatProjectCentroidPanel(),
  new PotentialNetworkClusterPanel(),
  new ScenarioNetworkClusterPanel(),
  new DistributionNetworkSegmentPanel(),
  new BaseDataPanel(),
];

const panelsByLayerId = {};
for (const panelObject of panelObjects) {
  for (const layerId of panelObject.layerIds) {
    panelsByLayerId[layerId] = panelObject;
  }
}
for (const layerId of Object.keys(layerConfig.value)) {
  if (layerConfig.value[layerId].autoGenerateSidebar) {
    panelsByLayerId[layerId] = new AutoGeneratedPanel(
      layerConfig.value[layerId].autoGenerateSidebarTitle,
    );
  }
}

const isDialogOpen = ref(false);
const dialogItemType = computed(() => {
  return panelData.value.name;
});

function remove() {
  panelData.value.customDeleteFunction(store);
  show.value = false;
}

function edit() {
  panelData.value.customEditFunction(store);
}

watch(
  [() => props.feature, featureRefresh],
  async ([newFeature]) => {
    // new feature was clicked or feature refresh was triggered by feature patch
    if (!newFeature) {
      show.value = false;
      return;
    }
    panel.value = panelsByLayerId[newFeature.layer.id];
    if (!panel.value) {
      console.log('no panel found for layer with id ', newFeature.layer.id);
      return;
    }

    featureData.value = await panel.value.requestFeatureData(
      newFeature,
      store.state.scenario.scenarioSelected.scenario_id,
    );
    if (
      props.feature.source === LAYER_KEY__HEAT_PROJECT_CENTROID &&
      heatProject.value
    ) {
      ProjectConnectionRate.value = heatProject.value.connectionRate;
    }
    setFilter(LAYER_KEY__HEAT_PROJECT_ESTATE, null);
    setFilter(LAYER_KEY__HEAT_PROJECT_NETWORK, null);
    highlightBuildings(true);
  },
  { immediate: true },
);

watch(
  [featureData, scenarioYear],
  async (
    [newFeatureData, newScenarioYear],
    [oldFeatureData, oldScenarioYear],
  ) => {
    // feature data or scenario year changed, thus recalculate panel data
    if (newFeatureData === null) return;
    if (newFeatureData === oldFeatureData && !show.value) {
      return;
    }

    panelData.value = panel.value.calculatePanelData(
      newFeatureData,
      newScenarioYear,
      store,
    );
    panelData.value.id = panelData.value.id
      ? panelData.value.id
      : props.featureId;
    show.value = true;
    if (props.feature.source === LAYER_KEY__HEAT_PROJECT_CENTROID) {
      breakEven.value = panelData.value.breakEven;
    }
  },
);

// Project planer

const ProjectConnectionRate = ref(null);
const breakEven = ref(null);

const heatProject = computed(() => {
  return store.state.heatProject.heatProjects.find(
    (hp) => hp.id === Number(props.featureId),
  );
});

watch(
  () => heatProject.value,
  (newHeatProject) => {
    if (newHeatProject) {
      heatProject.value.connectionRate =
        heatProject.value.originalConnectionRate;
    }
  },
);

function downloadProjectData(projectId) {
  window
    .open(`/api/heatprojects/project-download-csv/${projectId}/`, '_blank')
    .focus();
}

function applyToLayer(connectionRate) {
  if (ProjectConnectionRate.value === null) return;
  ProjectConnectionRate.value = connectionRate;
  const features = getMap().queryRenderedFeatures({
    layers: [LAYER_KEY__BUILDINGS],
    filter: ['in', 'alkis_id', ...heatProject.value.getBuildingIds],
  });
  features.forEach((feature) => {
    const alkisId = feature.properties.alkis_id;
    const connectionRateBuilding =
      heatProject.value.connectBuildingInterface.buildingByConnectionRate[
        alkisId
      ];
    const highlight = connectionRateBuilding <= connectionRate;
    getMap().setFeatureState(feature, { highlight });
  });
  let filter = ['<=', ['get', 'connectionRate'], parseFloat(connectionRate)];
  setFilter(LAYER_KEY__HEAT_PROJECT_ESTATE, [
    'any',
    ['!=', ['get', 'id'], heatProject.value.id],
    filter,
  ]);
  setFilter(LAYER_KEY__HEAT_PROJECT_NETWORK, [
    'any',
    ['!', ['has', 'connectionRate']],
    ['!=', ['get', 'id'], heatProject.value.id],
    filter,
  ]);
}

const tabContainerData = computed(() => {
  const buildings = heatProject.value.connectBuildingInterface
    .buildingCountByConnectionRate(ProjectConnectionRate.value)
    .toLocaleString('de-DE', { maximumFractionDigits: 0 });
  const minimumBuildingArea = heatProject.value.minimumBuildingArea;
  const publicBuildingPriority = heatProject.value.public_building_prio;
  return [
    { title: 'Gebäude', body: buildings },
    {
      title: 'ab',
      body: `${minimumBuildingArea.toLocaleString('de-DE', {
        maximumFractionDigits: 0,
      })} m²`,
    },
    {
      title: 'Öffentliche\n' + 'Liegenschaften',
      body: publicBuildingPriority ? 'prioritär' : 'nicht prioritär',
    },
  ];
});

watch(
  () => ProjectConnectionRate.value,
  (newVal) => {
    if (newVal === null || panelData.value === null) return;
    panelData.value = panel.value.calculatePanelDataConnectionRate(
      featureData.value,
      newVal,
    );
  },
);
</script>

<style lang="scss">
.info-panel-more-options {
  @apply w-full px-2 py-[5px] hover:bg-infra-background;
}
</style>
